/**
 * CEO Command Center - Task Components
 * 
 * Components for displaying and managing tasks:
 * - TaskCard: Individual task display
 * - TaskList: Grouped task list with filtering
 * - TaskEditor: Task creation/editing form
 * 
 * @module components/features/tasks
 * @version 10.0.0
 */

import React, { useState, useMemo } from 'react';
import { cn, formatDate, formatRelative, daysUntil } from '../../../utils/format';
import { Button, Badge, Modal, Input, Select, Textarea, Checkbox } from '../../ui';
import { DOMAIN_CONFIG, TASK_STATUSES, TASK_PRIORITIES } from '../../../config/constants';
import type { Task, TaskStatus, Priority, Domain, Owner } from '../../../types';

// TASK CARD

interface TaskCardProps {
  task: Task;
  onToggle?: (taskId: string) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
  onToggleSignal?: (taskId: string) => void;
  showDomain?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * Task card component
 * Displays a single task with status, priority, and actions
 */
export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onToggle,
  onEdit,
  onDelete,
  onToggleSignal,
  showDomain = true,
  compact = false,
  className,
}) => {
  const isOverdue = task.targetDate && daysUntil(task.targetDate) < 0 && task.status !== 'Done';
  const isDueToday = task.targetDate && daysUntil(task.targetDate) === 0 && task.status !== 'Done';
  const isDueSoon = task.targetDate && daysUntil(task.targetDate) > 0 && daysUntil(task.targetDate) <= 3 && task.status !== 'Done';
  
  const statusConfig: Record<TaskStatus, { icon: string; color: string }> = {
    'Not Started': { icon: '⚪', color: 'text-gray-500' },
    'In Progress': { icon: '🟡', color: 'text-amber-500' },
    'Done': { icon: '✅', color: 'text-green-500' },
  };
  
  const priorityConfig: Record<Priority, { color: string; bg: string }> = {
    'Critical': { color: 'text-red-700', bg: 'bg-red-100' },
    'High': { color: 'text-orange-700', bg: 'bg-orange-100' },
    'Medium': { color: 'text-amber-700', bg: 'bg-amber-100' },
    'Low': { color: 'text-gray-700', bg: 'bg-gray-100' },
  };
  
  const domain = DOMAIN_CONFIG[task.domain];
  
  if (compact) {
    return (
      <div className={cn(
        'flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 group',
        task.status === 'Done' && 'opacity-60',
        className
      )}>
        <button
          onClick={() => onToggle?.(task.id)}
          className="flex-shrink-0 text-lg"
        >
          {statusConfig[task.status].icon}
        </button>
        <span className={cn(
          'flex-1 truncate',
          task.status === 'Done' && 'line-through text-gray-400'
        )}>
          {task.title}
        </span>
        {task.isSignal && <span title="Signal">⭐</span>}
        {task.owner && (
          <span className="text-xs text-gray-400">{task.owner.split(' ')[0]}</span>
        )}
      </div>
    );
  }
  
  return (
    <div className={cn(
      'p-4 bg-white rounded-lg border hover:shadow-md transition-all group',
      isOverdue && 'border-l-4 border-l-red-500',
      task.isSignal && 'ring-2 ring-coral-500/30',
      task.status === 'Done' && 'opacity-60',
      className
    )}>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={() => onToggle?.(task.id)}
          className="flex-shrink-0 mt-0.5 text-xl"
          title={task.status === 'Done' ? 'Mark as not done' : 'Mark as done'}
        >
          {statusConfig[task.status].icon}
        </button>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className={cn(
              'font-medium text-gray-900',
              task.status === 'Done' && 'line-through text-gray-400'
            )}>
              {task.isSignal && <span className="mr-1">⭐</span>}
              {task.title}
            </h3>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onToggleSignal && (
                <button
                  onClick={() => onToggleSignal(task.id)}
                  className="p-1 text-gray-400 hover:text-amber-500 rounded"
                  title={task.isSignal ? 'Remove from signals' : 'Mark as signal'}
                >
                  {task.isSignal ? '⭐' : '☆'}
                </button>
              )}
              {onEdit && (
                <button
                  onClick={() => onEdit(task)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Edit"
                >
                  ✏️
                </button>
              )}
              {onDelete && (
                <button
                  onClick={() => onDelete(task.id)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded"
                  title="Delete"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
          
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-2 mt-2 text-sm">
            {showDomain && domain && (
              <Badge variant="default" size="sm">
                {domain.icon} {domain.name}
              </Badge>
            )}
            <Badge 
              variant={
                task.priority === 'Critical' ? 'danger' :
                task.priority === 'High' ? 'warning' :
                'default'
              }
              size="sm"
            >
              {task.priority}
            </Badge>
            {task.owner && (
              <span className="text-gray-500">{task.owner}</span>
            )}
            {task.targetDate && (
              <span className={cn(
                'flex items-center gap-1',
                isOverdue ? 'text-red-600 font-medium' :
                isDueToday ? 'text-amber-600 font-medium' :
                isDueSoon ? 'text-amber-500' :
                'text-gray-400'
              )}>
                {isOverdue ? '⏰ Overdue' : 
                 isDueToday ? '📅 Today' :
                 formatRelative(task.targetDate)}
              </span>
            )}
          </div>
          
          {/* Notes preview */}
          {task.notes && (
            <p className="mt-2 text-sm text-gray-500 line-clamp-2">
              {task.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// TASK LIST

interface TaskListProps {
  tasks: Task[];
  onToggle?: (taskId: string) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (taskId: string) => void;
  onToggleSignal?: (taskId: string) => void;
  groupBy?: 'status' | 'priority' | 'owner' | 'domain' | 'none';
  showDomain?: boolean;
  compact?: boolean;
  emptyMessage?: string;
  className?: string;
}

/**
 * Task list component
 * Displays tasks with optional grouping
 */
export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onToggle,
  onEdit,
  onDelete,
  onToggleSignal,
  groupBy = 'none',
  showDomain = true,
  compact = false,
  emptyMessage = 'No tasks',
  className,
}) => {
  const groupedTasks = useMemo(() => {
    if (groupBy === 'none') {
      return { '': tasks };
    }
    
    const groups: Record<string, Task[]> = {};
    
    tasks.forEach(task => {
      let key = '';
      switch (groupBy) {
        case 'status':
          key = task.status;
          break;
        case 'priority':
          key = task.priority;
          break;
        case 'owner':
          key = task.owner || 'Unassigned';
          break;
        case 'domain':
          key = task.domain;
          break;
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(task);
    });
    
    return groups;
  }, [tasks, groupBy]);
  
  if (tasks.length === 0) {
    return (
      <div className={cn('text-center py-8 text-gray-500', className)}>
        {emptyMessage}
      </div>
    );
  }
  
  // Define group order for different groupBy options
  const groupOrder: Record<string, string[]> = {
    status: ['In Progress', 'Not Started', 'Done'],
    priority: ['Critical', 'High', 'Medium', 'Low'],
    domain: Object.keys(DOMAIN_CONFIG),
  };
  
  const sortedGroups = groupBy !== 'none' && groupOrder[groupBy]
    ? groupOrder[groupBy].filter(k => groupedTasks[k]?.length > 0)
    : Object.keys(groupedTasks).sort();
  
  return (
    <div className={cn('space-y-6', className)}>
      {sortedGroups.map(group => (
        <div key={group}>
          {group && groupBy !== 'none' && (
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              {groupBy === 'status' && (
                <span>
                  {group === 'In Progress' ? '🟡' :
                   group === 'Done' ? '✅' : '⚪'}
                </span>
              )}
              {groupBy === 'domain' && DOMAIN_CONFIG[group as Domain]?.icon}
              {group}
              <span className="text-gray-400">({groupedTasks[group].length})</span>
            </h3>
          )}
          <div className="space-y-2">
            {groupedTasks[group].map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onToggle={onToggle}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleSignal={onToggleSignal}
                showDomain={showDomain && groupBy !== 'domain'}
                compact={compact}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// TASK EDITOR

interface TaskEditorProps {
  task?: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Partial<Task>) => Promise<void> | void;
  owners?: Owner[];
  defaultDomain?: Domain;
}

/**
 * Task editor modal
 * Used for creating and editing tasks
 */
export const TaskEditor: React.FC<TaskEditorProps> = ({
  task,
  isOpen,
  onClose,
  onSave,
  owners = [],
  defaultDomain = 'EMG',
}) => {
  const isNew = !task;
  
  const [formData, setFormData] = useState({
    title: task?.title || '',
    domain: task?.domain || defaultDomain,
    owner: task?.owner || '',
    status: task?.status || 'Not Started',
    priority: task?.priority || 'Medium',
    targetDate: task?.targetDate || '',
    notes: task?.notes || '',
    isSignal: task?.isSignal || false,
  });
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Reset form when task changes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        title: task?.title || '',
        domain: task?.domain || defaultDomain,
        owner: task?.owner || '',
        status: task?.status || 'Not Started',
        priority: task?.priority || 'Medium',
        targetDate: task?.targetDate || '',
        notes: task?.notes || '',
        isSignal: task?.isSignal || false,
      });
      setErrors({});
    }
  }, [task, isOpen, defaultDomain]);
  
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setSaving(true);
    try {
      await onSave({
        ...(task?.id && { id: task.id }),
        ...formData,
        targetDate: formData.targetDate || null,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save task:', error);
      // Extract actual error message
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'string' 
          ? error 
          : 'Failed to save task. Please try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setSaving(false);
    }
  };
  
  const updateField = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const domainOptions = Object.entries(DOMAIN_CONFIG).map(([key, config]) => ({
    value: key,
    label: `${config.icon} ${config.name}`,
  }));
  
  // Owner names for autocomplete datalist
  const ownerNames = owners.map(o => o.name);

  const statusOptions = TASK_STATUSES.map(s => ({ value: s, label: s }));
  const priorityOptions = TASK_PRIORITIES.map(p => ({ value: p, label: p }));
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isNew ? 'New Task' : 'Edit Task'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          value={formData.title}
          onChange={e => updateField('title', e.target.value)}
          placeholder="Enter task title"
          error={errors.title}
          autoFocus
        />
        
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Domain"
            value={formData.domain}
            onChange={e => updateField('domain', e.target.value)}
            options={domainOptions}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Owner
            </label>
            <input
              type="text"
              list="owner-list"
              value={formData.owner}
              onChange={e => updateField('owner', e.target.value)}
              placeholder="Type or select owner..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <datalist id="owner-list">
              {ownerNames.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
            {formData.owner && !ownerNames.includes(formData.owner) && (
              <p className="text-xs text-blue-600 mt-1">
                New owner "{formData.owner}" will be added to your team
              </p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Status"
            value={formData.status}
            onChange={e => updateField('status', e.target.value)}
            options={statusOptions}
          />
          
          <Select
            label="Priority"
            value={formData.priority}
            onChange={e => updateField('priority', e.target.value)}
            options={priorityOptions}
          />
          
          <Input
            label="Target Date"
            type="date"
            value={formData.targetDate}
            onChange={e => updateField('targetDate', e.target.value)}
          />
        </div>
        
        <Textarea
          label="Notes"
          value={formData.notes}
          onChange={e => updateField('notes', e.target.value)}
          placeholder="Add any additional notes..."
          rows={3}
        />
        
        <div className="flex items-center gap-4 pt-2">
          <Checkbox
            label="Mark as Signal (CEO attention required)"
            checked={formData.isSignal}
            onChange={e => updateField('isSignal', e.target.checked)}
          />
        </div>
        
        {errors.submit && (
          <p className="text-sm text-red-500">{errors.submit}</p>
        )}
        
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            {isNew ? 'Create Task' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// TASK QUICK ADD

interface TaskQuickAddProps {
  onAdd: (title: string, domain?: Domain) => Promise<void> | void;
  defaultDomain?: Domain;
  placeholder?: string;
  className?: string;
}

/**
 * Quick add task input
 * Simple inline form for fast task creation
 */
export const TaskQuickAdd: React.FC<TaskQuickAddProps> = ({
  onAdd,
  defaultDomain = 'EMG',
  placeholder = 'Add a task...',
  className,
}) => {
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) return;
    
    setAdding(true);
    try {
      await onAdd(title.trim(), defaultDomain);
      setTitle('');
    } finally {
      setAdding(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className={cn('flex gap-2', className)}>
      <Input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder={placeholder}
        className="flex-1"
        disabled={adding}
      />
      <Button type="submit" variant="primary" loading={adding} disabled={!title.trim()}>
        Add
      </Button>
    </form>
  );
};

